import type { ApiAppointment, FetchAppointmentsPage } from '../types';

/** Server maximum for `limit` (PageCriteria @Max(100) in the API). */
export const PRINT_PAGE_SIZE = 100;
/** Ceiling from the ticket: a neglected Action Required backlog must not print hundreds of pages. */
export const PRINT_ROW_CAP = 200;

interface LoadAllRowsOptions {
  pageSize?: number;
  cap?: number;
}

export interface LoadedRows {
  rows: ApiAppointment[];
  total: number;
  truncated: boolean;
}

/**
 * Follows the existing paginated endpoint until it runs out of pages or the cap is reached.
 * Rejects on any failed page so a partial list can never print silently.
 */
export async function loadAllRows(
  fetchPage: FetchAppointmentsPage,
  { pageSize = PRINT_PAGE_SIZE, cap = PRINT_ROW_CAP }: LoadAllRowsOptions = {}
): Promise<LoadedRows> {
  const rows: ApiAppointment[] = [];
  let total = 0;
  let page = 1;

  for (;;) {
    // eslint-disable-next-line no-await-in-loop -- pages are sequential by design
    const result = await fetchPage(page, pageSize);
    rows.push(...result.appointments);
    total = result.pagination.total;

    const exhausted = !result.pagination.links.next || result.appointments.length === 0;
    if (exhausted || rows.length >= cap) break;
    page += 1;
  }

  const kept = rows.slice(0, cap);
  return { rows: kept, total, truncated: kept.length < total };
}
